import React, { useState, useRef, useCallback } from 'react';
import FullBlockingModal from '../common/FullBlockingModal';
import { useTranslation } from 'react-i18next';
import { QuoteOwner, Quote, QuoteJob, QuoteTaskInput } from '../../types/quote';
import { QuoteJobInfo } from './QuoteJobInfo';
import OfferLinesEditor, { OfferLinesEditorHandle } from './OfferLinesEditor';
import MainSubmission from './MainSubmission';
import { Job } from '../../types/job';
import ReportButtonMenu, { BottomSuccessMessage } from '../reports/ReportButtonMenu';
import ReportModal from '../reports/ReportModal';

interface UpsertQuoteModalProps {
  job: QuoteJob | Job;
  quote: Quote;
  parentQuoteOwner?: QuoteOwner | null;
  parentQuoteId?: string | null;
  onClose: () => void;
}

const UpsertQuoteModal: React.FC<UpsertQuoteModalProps> = ({
  quote,
  job,
  parentQuoteOwner,
  parentQuoteId,
  onClose,
}) => {
  const { t } = useTranslation('quotes');

  const [copied, setCopied] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const editorSaveRef = useRef<OfferLinesEditorHandle>(null);

  const handleSaveOffer = useCallback(async (): Promise<boolean> => {
    const result = await editorSaveRef.current?.save();
    return result ?? false;
  }, []);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lines: QuoteTaskInput[] =
    quote?.tasks.map(task => ({
      id: task.id,
      name: task.name,
      pricing_type: task.pricing_type,
      quantity: task.quantity,
      unit_price: task.unit_price,
    })) ?? [];

  const canModify =
    quote?.status === 'draft' ||
    (quote?.status === 'submitted' && !!quote?.change_request_message?.trim());

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[72rem]">
        <div className="flex flex-row items-center">
          <div className="absolute top-2.5 right-1 text-gray-400 hover:text-gray-600 text-2xl  md:right-8">
            <ReportButtonMenu
              onReport={() => setShowReportModal(true)}
              entityOwner={job.client.id}
              onCopySuccess={handleCopySuccess}
              contentTypeTitle="report_this_quote"
            />
          </div>

          <button
            className="absolute -top-1 right-1 text-gray-400 hover:text-gray-600 text-2xl md:top-0 md:right-4"
            onClick={onClose}
            aria-label={t('common:form_actions.close')}
          >
            &times;
          </button>
        </div>

        <div className="space-y-4">
          <QuoteJobInfo quoteJob={quote ? quote.job : job!} />
          <hr className="border-[#E1E9F0]" />
          <h3 className="text-xl font-bold text-secondary">
            {t('createQuoteModal.addOfferSectionTitle', 'Mon offre')}
          </h3>

          {quote?.change_request_message?.trim() && (
            <div className="rounded-lg border border-[#E1E9F0] bg-[#E1E9F0]/30 px-4 py-3">
              <h4 className="text-xs font-medium uppercase tracking-wide text-secondary mb-1">
                {t('createQuoteModal.changeRequestLabel', 'Demande d’ajustement')}
              </h4>
              <p className="text-sm text-secondary whitespace-pre-wrap font-ambit">
                {quote.change_request_message}
              </p>
            </div>
          )}

          <OfferLinesEditor
            ref={editorSaveRef}
            initialLines={lines}
            initialNotes={quote?.message ?? null}
            job={job}
            quote={quote}
            parentQuoteId={parentQuoteId ?? quote?.parent_quote_id ?? null}
            canModify={!!canModify}
            onDirtyChange={setEditorDirty}
          />

          <hr className="border-[#E1E9F0]" />

          <MainSubmission
            parentQuoteOwner={parentQuoteOwner ?? quote?.parent_quote_owner ?? null}
            parentQuoteId={parentQuoteId ?? quote?.parent_quote_id ?? null}
            job={job}
            quote={quote}
            onClose={onClose}
            hasUnsavedOfferChanges={editorDirty}
            onSaveOffer={handleSaveOffer}
          />
        </div>
      </FullBlockingModal>

      {showReportModal && quote && (
        <ReportModal
          entityId={quote.id}
          reportedId={quote.job.client.id}
          contentType="quote"
          onClose={() => setShowReportModal(false)}
        />
      )}

      {copied && <BottomSuccessMessage />}
    </>
  );
};

export default UpsertQuoteModal;
